"use client";
import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';

interface ServicesSectionProps {
  isVisible: boolean;
  onVisibilityChange: (visible: boolean) => void;
}

export default function ServicesSection({ isVisible, onVisibilityChange }: ServicesSectionProps) {
  const sectionRef = useRef(null);
  const t = useTranslations();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisibilityChange(true);
        }
      },
      {
        threshold: 0.02,
        rootMargin: '0px 0px 50px 0px'
      }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [onVisibilityChange]);

  return (
    <>
      {/* Services Section Styles */}
      <style dangerouslySetInnerHTML={{__html: `
        .services-bg-1 {
          filter: blur(150px);
        }
        
        .destinations-bg-1, .destinations-bg-2 {
          filter: blur(110px);
        }
        
        .destinations-bg-2 {
          filter: blur(120px);
        }
        
        @media (max-width: 768px) {
          .services-grid {
            column-count: 1 !important;
            max-width: 100% !important;
          }
          
          .destinations-grid {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
            text-align: center !important;
            padding: 40px 20px !important;
          }
          
          .destinations-truck-container {
            display: flex !important;
            justify-content: center !important;
            align-items: center !important;
            position: relative !important;
          }
          
          .destinations-truck-image {
            max-width: 90% !important;
            height: auto !important;
          }
          
          .destinations-mjc-blur {
            width: 300px !important;
            height: 150px !important;
            top: -30px !important;
            left: 50% !important;
            transform: translateX(-50%) !important;
          }
          
          .section-padding {
            padding: 80px 12px !important;
          }
          
          .services-bg-1 {
            filter: none !important;
            background: radial-gradient(circle at center, rgba(217, 13, 52, 0.3) 0%, rgba(217, 13, 52, 0.2) 20%, rgba(217, 13, 52, 0.12) 40%, rgba(217, 13, 52, 0.06) 60%, rgba(217, 13, 52, 0.03) 80%, transparent 100%) !important;
          }
          
          .destinations-bg-1, .destinations-bg-2 {
            filter: none !important;
            border-radius: 50% !important;
            transform: none !important;
          }
          
          .destinations-bg-1 {
            width: 800px !important;
            height: 800px !important;
            left: -300px !important;
            bottom: 50px !important;
            background: radial-gradient(circle at center, 
              rgba(255, 20, 60, 0.6) 0%, 
              rgba(255, 20, 60, 0.4) 12%, 
              rgba(217, 13, 52, 0.25) 25%, 
              rgba(217, 13, 52, 0.15) 40%, 
              rgba(217, 13, 52, 0.08) 55%, 
              rgba(217, 13, 52, 0.04) 70%, 
              rgba(217, 13, 52, 0.02) 85%, 
              transparent 100%) !important;
          }
          
          .destinations-bg-2 {
            width: 800px !important;
            height: 800px !important;
            right: -300px !important;
            bottom: 50px !important;
            background: radial-gradient(circle at center, 
              rgba(255, 40, 60, 0.6) 0%, 
              rgba(255, 40, 60, 0.4) 12%, 
              rgba(181, 31, 36, 0.25) 25%, 
              rgba(181, 31, 36, 0.15) 40%, 
              rgba(181, 31, 36, 0.08) 55%, 
              rgba(181, 31, 36, 0.04) 70%, 
              rgba(181, 31, 36, 0.02) 85%, 
              transparent 100%) !important;
          }
        }

        @media (max-width: 480px) {
          .section-title {
            font-size: 32px !important;
          }
          
          .section-padding {
            padding: 60px 16px !important;
          }
        }
      `}} />

      <section 
        ref={sectionRef} 
        style={{
          background: "#000",
          padding: "80px 24px",
          position: "relative",
          overflow: "hidden",
        }}
        className="section-padding"
      >
        {/* Background gradient ellipses */}
        <div 
          className="services-bg-1"
          style={{
            position: "absolute",
            left: "50%",
            top: "300px",
            transform: "translateX(-50%)",
            width: "600px",
            height: "400px",
            background: "radial-gradient(50% 50% at 50% 50%, #D90D34 0%, rgba(217, 13, 52, 0) 100%)",
            zIndex: 1,
            opacity: isVisible ? 0.3 : 0,
            transition: "opacity 1.5s ease-out",
            transitionDelay: "0.2s",
          }}
        />

        <div style={{
          maxWidth: "900px",
          margin: "0 auto",
          position: "relative",
          zIndex: 10,
        }}>
          {/* Title and Description */}
          <div style={{ 
            textAlign: "center", 
            marginBottom: "80px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          }}>
            <h2 style={{
              fontSize: "42px",
              fontWeight: 600,
              color: "#fff",
              lineHeight: 1.2,
              marginBottom: "32px",
              maxWidth: "1000px",
              margin: "0 auto 32px auto",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.05s",
            }}
            className="section-title">
              {t('oldFurnitureRemoval.services.title')}
            </h2>
            
            <p style={{
              fontSize: "18px",
              color: "rgba(255,255,255,0.85)",
              lineHeight: 1.6,
              maxWidth: "800px",
              margin: "0 auto",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.1s",
            }}>
              {t('oldFurnitureRemoval.services.subtitle')}
            </p>
            
            <h3 style={{
              fontSize: "28px",
              fontWeight: 500,
              color: "#fff",
              marginTop: "40px",
              maxWidth: "800px",
              margin: "40px auto 0 auto",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.15s",
            }}>
              {t('oldFurnitureRemoval.services.furnitureTypes.title')}
            </h3>
          </div>

          {/* 3x3 Grid */}
          <div style={{
            columnCount: 3,
            columnGap: "32px",
            marginBottom: "32px",
            maxWidth: "900px",
            margin: "60px auto 0 auto",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',
            transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.2s",
          }}
          className="services-grid">
            {/* Service Box 1 - Apartments */}
            <div style={{
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "70px 24px 40px 24px",
              marginBottom: "32px",
              breakInside: "avoid",
              position: "relative",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
              transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.25s",
            }}>
              <div style={{
                position: "absolute",
                top: "16px",
                left: "16px",
                width: "40px",
                height: "40px",
              }}>
                <Image
                  src="/images/components/international/red-circle.png"
                  alt="Circle background"
                  width={40}
                  height={40}
                  style={{ position: "absolute", top: 0, left: 0 }}
                />
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "20px",
                  height: "20px",
                }}>
                  <div style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}>
                    <Image
                      src="/images/components/international/house.png"
                      alt="House icon"
                      fill
                      unoptimized
                      style={{ 
                        objectFit: "contain"
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#fff",
                  marginBottom: "12px",
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.removal.title')}
                </h3>
                <p style={{
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.removal.description')}
                </p>
              </div>
            </div>

            {/* Service Box 2 - Houses */}
            <div style={{
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "70px 24px 40px 24px",
              marginBottom: "32px",
              breakInside: "avoid",
              position: "relative",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0px)' : 'translateX(-40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.3s",
            }}>
              <div style={{
                position: "absolute",
                top: "16px",
                left: "16px",
                width: "40px",
                height: "40px",
              }}>
                <Image
                  src="/images/components/international/red-circle.png"
                  alt="Circle background"
                  width={40}
                  height={40}
                  style={{ position: "absolute", top: 0, left: 0 }}
                />
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <Image
                    src="/images/components/international/house.png"
                    alt="House icon"
                    width={20}
                    height={20}
                    style={{ 
                      width: "auto",
                      height: "auto",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain"
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#fff",
                  marginBottom: "12px",
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.dismantling.title')}
                </h3>
                <p style={{
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.dismantling.description')}
                </p>
              </div>
            </div>

            {/* Service Box 3 - Offices */}
            <div style={{
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "70px 24px 40px 24px",
              marginBottom: "32px",
              breakInside: "avoid",
              position: "relative",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0px)' : 'translateY(30px)',  
              transition: 'all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.4s",
            }}>
              <div style={{
                position: "absolute",
                top: "16px",
                left: "16px",
                width: "40px",
                height: "40px",
              }}>
                <Image
                  src="/images/components/international/red-circle.png"
                  alt="Circle background"
                  width={40}
                  height={40}
                  style={{ position: "absolute", top: 0, left: 0 }}
                />
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <Image
                    src="/images/components/international/doc.png"
                    alt="Document icon"
                    width={20}
                    height={20}
                    style={{ 
                      width: "auto",
                      height: "auto",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain"
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#fff",
                  marginBottom: "12px",
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.carrying.title')}
                </h3>
                <p style={{
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.carrying.description')}
                </p>
              </div>
            </div>

            {/* Service Box 4 - Storage */}
            <div style={{
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "70px 24px 40px 24px",
              marginBottom: "32px",
              breakInside: "avoid",
              position: "relative",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateX(0px)' : 'translateX(40px)',
              transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.5s",
            }}>
              <div style={{
                position: "absolute",
                top: "16px",
                left: "16px",
                width: "40px",
                height: "40px",
              }}>
                <Image
                  src="/images/components/international/red-circle.png"
                  alt="Circle background"
                  width={40}
                  height={40}
                  style={{ position: "absolute", top: 0, left: 0 }}
                />
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "20px",
                  height: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <Image
                    src="/images/components/international/house.png"
                    alt="House icon"
                    width={20}
                    height={20}
                    style={{ 
                      width: "auto",
                      height: "auto",
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain"
                    }}
                  />
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#fff",
                  marginBottom: "12px",
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.transportation.title')}
                </h3>
                <p style={{
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.transportation.description')}
                </p>
              </div>
            </div>

            {/* Service Box 5 - Furniture */}
            <div style={{
              background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
              border: "1px solid #262626",
              borderRadius: "12px",
              padding: "70px 24px 40px 24px",
              marginBottom: "32px",
              breakInside: "avoid",
              position: "relative",
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
              transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
              transitionDelay: "0.6s",
            }}>
              <div style={{
                position: "absolute",
                top: "16px",
                left: "16px",
                width: "40px",
                height: "40px",
              }}>
                <Image
                  src="/images/components/international/red-circle.png"
                  alt="Circle background"
                  width={40}
                  height={40}
                  style={{ position: "absolute", top: 0, left: 0 }}
                />
                <div style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: "20px",
                  height: "20px",
                }}>
                  <div style={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                  }}>
                    <Image
                      src="/images/components/international/sofa.png"
                      alt="Sofa icon"
                      fill
                      unoptimized
                      style={{ 
                        objectFit: "contain"
                      }}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <h3 style={{
                  fontSize: "16px",
                  fontWeight: 500,
                  color: "#fff",
                  marginBottom: "12px",
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.disposal.title')}
                </h3>
                <p style={{
                  fontSize: "13px",
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: 1.5,
                }}>
                  {t('oldFurnitureRemoval.services.servicesList.disposal.description')}
                </p>
              </div>
            </div>




          </div>
        </div>

        {/* FURNITURE TYPES SECTION - Red Mercedes Container - Outside main container for full width */}
        <div style={{
          maxWidth: "1400px",
          margin: "0 auto",
          position: "relative",
          zIndex: 10,
          padding: "0 24px",
        }}>
          {/* Main Furniture Types Section */}
          <div style={{
            position: "relative",
            marginTop: "80px",
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
            transition: 'all 0.9s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
            transitionDelay: "0.2s",
          }}>
            {/* Blurred Background */}
            <div style={{
              position: "absolute",
              top: "0",
              left: "0",
              right: "0",
              bottom: "0",
              background: "rgba(17, 27, 34, 0.3)",
              border: "1px solid rgba(255, 0, 0, 0.1)",
              borderRadius: "16px",
              zIndex: 1,
            }}></div>

            {/* Left Red Ellipse Behind */}
            <div 
              className="furniture-types-bg-1"
              style={{
                position: "absolute",
                left: "-100px",
                bottom: "250px",
                width: "700px",
                height: "400px",
                background: "radial-gradient(50% 50% at 50% 50%, #D90D34 90%, rgba(217, 13, 52, 0) 100%)",
                transform: "rotate(-45deg)",
                zIndex: 0,
                opacity: isVisible ? 0.4 : 0,
                transition: "opacity 2s ease-out",
                transitionDelay: "0.3s",
              }}
            ></div>

            {/* Right Red Ellipse Behind */}
            <div 
              className="furniture-types-bg-2"
              style={{
                position: "absolute",
                right: "-100px",
                bottom: "250px",
                width: "700px",
                height: "400px",
                background: "radial-gradient(50% 50% at 50% 50%, #B51F24 70%, rgba(181, 31, 36, 0) 100%)",
                transform: "rotate(45deg)",
                zIndex: 0,
                opacity: isVisible ? 0.4 : 0,
                transition: "opacity 2s ease-out",
                transitionDelay: "0.4s",
              }}
            ></div>
            
            {/* Content Container */}
            <div style={{
              position: "relative",
              padding: "60px",
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "60px",
              alignItems: "center",
              zIndex: 2,
            }}
            className="furniture-types-grid">
              {/* Left side - Red Mercedes Image */}
              <div style={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0px)' : 'translateX(-40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.3s",
              }}>
                {/* MJC Blur Image - Behind the truck */}
                <div style={{
                  position: "absolute",
                  top: "-40px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  zIndex: 1,
                }}
                className="furniture-types-mjc-blur">
                  <Image
                    src="/images/components/mjc-blur.png"
                    alt="MJC Blur"
                    width={400}
                    height={200}
                    style={{ 
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
                
                <Image
                  src="/images/components/merc-red.png"
                  alt="Furniture Removal Service Truck"
                  width={600}
                  height={400}
                  className="furniture-types-truck-image"
                  style={{ 
                    width: "100%",
                    height: "auto",
                    position: "relative",
                    zIndex: 2,
                    maxWidth: "100%",
                  }}
                />
              </div>

              {/* Right side - Furniture Types Content */}
              <div style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateX(0px)' : 'translateX(40px)',
                transition: 'all 1s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                transitionDelay: "0.4s",
              }}>
                <h3 style={{
                  fontSize: "32px",
                  fontWeight: 600,
                  color: "#fff",
                  marginBottom: "16px",
                  lineHeight: 1.2,
                }}>
                  {t('oldFurnitureRemoval.services.furnitureTypes.title')}
                </h3>
                
                <p style={{
                  fontSize: "16px",
                  color: "rgba(255,255,255,0.85)",
                  marginBottom: "40px",
                  lineHeight: 1.6,
                }}>
                  Išvežame visų tipų senus baldus iš bet kokių patalpų – nuo butų iki biurų.
                </p>

                {/* Furniture Type boxes */}
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                }}>
                  {/* Upholstered Furniture Box */}
                  <div 
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "linear-gradient(70.15deg, rgba(0, 0, 0, 0.7) 8.41%, rgba(136, 1, 5, 0.7) 88.21%)";
                      e.currentTarget.style.border = "none";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)";
                      e.currentTarget.style.border = "1px solid #262626";
                    }}
                    style={{
                      background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                      border: "1px solid #262626",
                      borderRadius: "12px",
                      padding: "24px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      <Image
                        src="/images/components/international/sofa.png"
                        alt="Sofa icon"
                        width={20}
                        height={20}
                        style={{ marginTop: "2px", flexShrink: 0 }}
                      />
                      <div>
                        <h4 style={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#fff",
                          marginBottom: "12px",
                        }}>
                          {t('oldFurnitureRemoval.services.furnitureTypes.upholsteredTitle')}
                        </h4>
                        <p style={{
                          fontSize: "14px",
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.5,
                          margin: 0,
                        }}>
                          {t('oldFurnitureRemoval.services.furnitureTypes.upholstered')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Case Goods Box */}
                  <div 
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "linear-gradient(70.15deg, rgba(0, 0, 0, 0.7) 8.41%, rgba(136, 1, 5, 0.7) 88.21%)";
                      e.currentTarget.style.border = "none";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)";
                      e.currentTarget.style.border = "1px solid #262626";
                    }}
                    style={{
                      background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                      border: "1px solid #262626",
                      borderRadius: "12px",
                      padding: "24px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      <Image
                        src="/images/components/international/box.png"
                        alt="Box icon"
                        width={20}
                        height={20}
                        style={{ marginTop: "2px", flexShrink: 0 }}
                      />
                      <div>
                        <h4 style={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#fff",
                          marginBottom: "12px",
                        }}>
                          {t('oldFurnitureRemoval.services.furnitureTypes.caseGoodsTitle')}
                        </h4>
                        <p style={{
                          fontSize: "14px",
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.5,
                          margin: 0,
                        }}>
                          {t('oldFurnitureRemoval.services.furnitureTypes.caseGoods')}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Office Furniture Box */}
                  <div 
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "linear-gradient(70.15deg, rgba(0, 0, 0, 0.7) 8.41%, rgba(136, 1, 5, 0.7) 88.21%)";
                      e.currentTarget.style.border = "none";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)";
                      e.currentTarget.style.border = "1px solid #262626";
                    }}
                    style={{
                      background: "linear-gradient(99.27deg, #000000 30.92%, #0B0B0B 93.71%)",
                      border: "1px solid #262626",
                      borderRadius: "12px",
                      padding: "24px",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                    }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: "12px" }}>
                      <Image
                        src="/images/components/international/doc.png"
                        alt="Document icon"
                        width={20}
                        height={20}
                        style={{ marginTop: "2px", flexShrink: 0 }}
                      />
                      <div>
                        <h4 style={{
                          fontSize: "18px",
                          fontWeight: 600,
                          color: "#fff",
                          marginBottom: "12px",
                        }}>
                          {t('oldFurnitureRemoval.services.furnitureTypes.officeTitle')}
                        </h4>
                        <p style={{
                          fontSize: "14px",
                          color: "rgba(255,255,255,0.8)",
                          lineHeight: 1.5,
                          margin: 0,
                        }}>
                          {t('oldFurnitureRemoval.services.furnitureTypes.office')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
} 